/**
 * @deprecated Use usePractice instead. This hook is kept for backward
 * compatibility with CheckInFlow.tsx.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { db, type PracticeEntry } from "@/lib/db";
import { useAuth } from "@/hooks/useAuth";

// ---------------------------------------------------------------------------
// Time Slot Configuration
// ---------------------------------------------------------------------------

export interface TimeSlotConfig {
  slot: 1 | 2 | 3 | 4 | 5 | 6;
  label: string;
  labelShort: string;
  startHour: number;
  endHour: number;
}

export const TIME_SLOTS: TimeSlotConfig[] = [
  { slot: 1, label: "Früh (05:00–08:00)", labelShort: "Früh", startHour: 5, endHour: 8 },
  { slot: 2, label: "Morgen (08:00–11:00)", labelShort: "Morgen", startHour: 8, endHour: 11 },
  { slot: 3, label: "Mittag (11:00–14:00)", labelShort: "Mittag", startHour: 11, endHour: 14 },
  { slot: 4, label: "Nachmittag (14:00–17:00)", labelShort: "Nachmittag", startHour: 14, endHour: 17 },
  { slot: 5, label: "Abend (17:00–20:00)", labelShort: "Abend", startHour: 17, endHour: 20 },
  { slot: 6, label: "Nacht (20:00–00:00)", labelShort: "Nacht", startHour: 20, endHour: 24 },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTodayString(): string {
  const now = new Date();
  return now.toISOString().slice(0, 10); // YYYY-MM-DD
}

export function getCurrentTimeSlot(): TimeSlotConfig {
  const hour = new Date().getHours();

  for (const ts of TIME_SLOTS) {
    if (hour >= ts.startHour && hour < ts.endHour) {
      return ts;
    }
  }

  // Before 05:00 => treat as slot 6 (night) from previous logical day,
  // but for UX we show slot 1 as "next up"
  return TIME_SLOTS[0];
}

export function getTimeSlotStatus(
  slot: TimeSlotConfig,
  completedSlots: Set<number>,
  currentSlot: number
): "completed" | "current" | "upcoming" | "missed" {
  if (completedSlots.has(slot.slot)) return "completed";
  if (slot.slot === currentSlot) return "current";
  if (slot.slot < currentSlot) return "missed";
  return "upcoming";
}

// ---------------------------------------------------------------------------
// Journal Entry input type (legacy — maps to PracticeEntry internally)
// ---------------------------------------------------------------------------

export interface JournalEntryInput {
  positive: string;
  negative: string;
  virtueId: number | null;
  widmung: string;
  versprechen: string;
  versprechenBisDatum: string | null;
}

// ---------------------------------------------------------------------------
// Hook (legacy — uses practiceEntries table under the hood)
// ---------------------------------------------------------------------------

export function useCheckIn() {
  const { user } = useAuth();
  const [todayEntries, setTodayEntries] = useState<PracticeEntry[]>([]);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);

  const userId = user?.id ?? "";
  const today = getTodayString();

  // Load today's entries from practiceEntries
  const loadTodayEntries = useCallback(async () => {
    if (!userId) return;
    try {
      const records = await db.practiceEntries
        .where("[userId+date]")
        .equals([userId, today])
        .toArray();
      setTodayEntries(records);
    } catch (err) {
      console.error("Failed to load today's practice entries:", err);
    }
  }, [userId, today]);

  // Calculate streak
  const loadStreak = useCallback(async () => {
    if (!userId) return;
    try {
      let currentStreak = 0;
      const date = new Date();

      const todayRecords = await db.practiceEntries
        .where("[userId+date]")
        .equals([userId, getTodayString()])
        .count();

      if (todayRecords > 0) {
        currentStreak = 1;
      }

      for (let i = 1; i <= 365; i++) {
        const checkDate = new Date(date);
        checkDate.setDate(checkDate.getDate() - i);
        const dateStr = checkDate.toISOString().slice(0, 10);

        const count = await db.practiceEntries
          .where("[userId+date]")
          .equals([userId, dateStr])
          .count();

        if (count > 0) {
          currentStreak++;
        } else {
          break;
        }
      }

      setStreak(currentStreak);
    } catch (err) {
      console.error("Failed to calculate streak:", err);
    }
  }, [userId]);

  // Save a journal entry (creates two PracticeEntry records if both positive and negative are filled)
  const saveJournalEntry = useCallback(
    async (timeSlot: 1 | 2 | 3 | 4 | 5 | 6, input: JournalEntryInput) => {
      if (!userId) throw new Error("User not authenticated");

      const now = new Date().toISOString();

      // Create positive entry if positive text exists
      if (input.positive.trim()) {
        const positiveRecord: PracticeEntry = {
          id: crypto.randomUUID(),
          userId,
          date: today,
          timeSlot,
          type: "positive",
          text: input.positive,
          virtueIds: input.virtueId ? [input.virtueId] : undefined,
          widmung: input.widmung,
          versprechen: "",
          completedAt: now,
        };
        await db.practiceEntries.add(positiveRecord);
      }

      // Create negative entry if negative text exists
      if (input.negative.trim()) {
        const negativeRecord: PracticeEntry = {
          id: crypto.randomUUID(),
          userId,
          date: today,
          timeSlot,
          type: "negative",
          text: input.negative,
          virtueIds: input.virtueId ? [input.virtueId] : undefined,
          widmung: "",
          versprechen: input.versprechen,
          versprechenBisDatum: input.versprechenBisDatum ?? undefined,
          versprechenErfuellt: false,
          completedAt: now,
        };
        await db.practiceEntries.add(negativeRecord);
      }

      await loadTodayEntries();
      await loadStreak();
    },
    [userId, today, loadTodayEntries, loadStreak]
  );

  // Derived state
  const completedSlots = useMemo(
    () => new Set(todayEntries.map((c) => c.timeSlot)),
    [todayEntries]
  );

  const completedCount = completedSlots.size;

  const currentTimeSlot = useMemo(() => getCurrentTimeSlot(), []);

  const nextPendingSlot = useMemo(() => {
    for (const ts of TIME_SLOTS) {
      if (ts.slot >= currentTimeSlot.slot && !completedSlots.has(ts.slot)) {
        return ts;
      }
    }
    for (const ts of TIME_SLOTS) {
      if (!completedSlots.has(ts.slot)) {
        return ts;
      }
    }
    return null;
  }, [currentTimeSlot.slot, completedSlots]);

  // Initial load
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.all([loadTodayEntries(), loadStreak()]).finally(() =>
      setLoading(false)
    );
  }, [userId, loadTodayEntries, loadStreak]);

  return {
    todayEntries,
    completedSlots,
    completedCount,
    streak,
    currentTimeSlot,
    nextPendingSlot,
    loading,
    saveJournalEntry,
    reload: loadTodayEntries,
  };
}
